import { useIsMutating, useMutation } from "@tanstack/solid-query";
import { createContext, useContext } from "solid-js";
import { saveWorkspace } from "~/js/workspace";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { getWorkspacesStore } from "~/solidJs/entities/WorkspaceListEntity/models/wrokspaceListContext";
import {
	showToastMessage,
	ToastMessageTypes
} from "~/solidJs/widgets/WorkspaceToast/WorkspaceToast";
import { getPublicMessageFromError } from "../helpers";
import { UserSubscriptionPlanEnum } from "../helpers/types";

// Context for sharing the mutation across components
const SaveWorkspaceContext = createContext<ReturnType<typeof useSaveWorkspaceMutation>>();

export const SaveWorkspaceProvider = (props: { children: any }) => {
	const saveMutation = useSaveWorkspaceMutation();

	return (
		<SaveWorkspaceContext.Provider value={saveMutation}>
			{props.children}
		</SaveWorkspaceContext.Provider>
	);
};

// Global save workspace mutation hook
const useSaveWorkspaceMutation = () => {
	return useMutation(() => ({
		mutationKey: ["saveWorkspace"],
		mutationFn: async (params?: {
			afterSaveCallback?: VoidFunction;
			skipShowSavedModal?: boolean;
			cancelCallback?: VoidFunction;
		}) => {
			await saveWorkspace(
				params?.afterSaveCallback,
				params?.skipShowSavedModal,
				params?.cancelCallback
			);
		},
		scope: {
			id: "workspace"
		},
		onError(e) {
			showToastMessage({
				type: ToastMessageTypes.Error,
				title: getPublicMessageFromError(e) || "An error occurred",
				timeout: 2000
			});
		}
	}));
};

export const useSaveWorkspaceFeature = (props?: {
	afterSaveCallback?: VoidFunction;
	skipShowSavedModal?: boolean;
	cancelCallback?: VoidFunction;
}) => {
	// Try to get the shared mutation from context first
	const sharedMutation = useContext(SaveWorkspaceContext);

	// Fallback to creating a new mutation if no context is available
	const fallbackMutation = useSaveWorkspaceMutation();

	const saveMutation = sharedMutation || fallbackMutation;

	const mutations = useIsMutating(() => ({
		mutationKey: ["saveWorkspace"]
	}));

	const getIsDisabled = () => {
		const store = getWorkspacesStore();
		if (getUserStore()?.group === UserSubscriptionPlanEnum.Superuser) {
			return store.isOpenPending || saveMutation.isPending;
		}
		return store.isOpenedByLink || store.isOpenPending || saveMutation.isPending;
	};

	const save = () => {
		saveMutation.mutate(props);
	};

	return {
		getIsDisabled,
		getIsPending: () => mutations() > 0,
		save,
		mutation: saveMutation
	};
};
